import { nsXsi } from '@qdabra/forms-shared/xmlNamespaces';
import { isEmpty } from 'ramda';
import { copyNodeContent } from '../../utility/xmlHelpers';
import { getChildElements, setNodeValue } from '../../xmlUtility';
import {
    paramDsNameDest,
    paramDsNameSrc,
    paramEmpty,
    paramRowDest,
    paramRowSrc,
    paramTableDest,
    paramTableSrc,
    paramXpathDest,
} from './constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from './constants/parameterTypes';
import { success } from './helpers/commandResult';
import {
    deleteNodes,
    getValidNode,
    getValidNodeSet,
} from './helpers/qRules.node.helpers';
import {
    selectNodeFromXPathEngine,
    selectNodeListFromXPathEngine,
} from './helpers/qRules.utility';
import { XHTML_NAMESPACE } from '../../utility/richTextHelpers';

const optionalParameters = [
    {
        name: paramDsNameSrc,
        description: 'dsnamesrc',
        type: typeDataSource,
    },
    {
        name: paramDsNameDest,
        description: 'dsnamedest',
        type: typeDataSource,
    },
    {
        name: paramXpathDest,
        description: 'xpathdest',
        type: typeXPath,
        dsParentName: paramDsNameDest,
    },
    {
        name: paramEmpty,
        description: 'empty',
        type: typeBoolean,
    },
];
const requiredParameters = [
    {
        name: paramTableSrc,
        description: 'tablesrc',
        type: typeXPath,
        dsParentName: paramDsNameSrc,
    },
    {
        name: paramRowSrc,
        description: 'rowsrc',
        type: typeXPath,
        dsParentName: paramDsNameSrc,
    },
    {
        name: paramTableDest,
        description: 'tabledest',
        type: typeXPath,
        dsParentName: paramDsNameDest,
    },
    {
        name: paramRowDest,
        description: 'rowdest',
        type: typeXPath,
        dsParentName: paramDsNameDest,
    },
];
const copyTableErrorFailedToSelectSourceTable =
    'Failed to select the source table to {0}.';
const copyTableErrorFailedToSelectDestinationTable =
    'Failed to select the destination table to {0} to.';
const copyTableErrorFailedToSelectSourceRow =
    'Failed to select any source rows to {0}.';
const copyTableErrorFailedToSelectDestinationRow =
    'Failed to select the destination row to {0} to.';
const copyTableErrorStructureMismatch =
    'The table row of the source and destination must contain the same number of elements and attributes.';
const copyTableErrorDeepTables =
    'Copy Table does not work with tables containing groups. Instead, use the Insert command and then XPath or SetValue to populate your values.';
const copyTableErrorFailedToAdd =
    'Failed to add copied rows to destination table.';
const copyTableErrorFailedToDelete = 'Failed to empty rows from source table.';
const isMove = false; //TODO:Check as its implemented for CopyTable
const cmd = isMove ? 'move' : 'copy';
const description =
    'Copies data from the source table to the destination table.';

function createError(name, message, stackMessage) {
    var error = new Error(message);
    error.name = name;

    //TODO:Confirm stack message implemented correctly, not used anywhere?
    if (stackMessage) {
        error.stack = stackMessage;
    }

    return error;
}

function CopyTable(params) {
    var cf = params.commonFunctions;

    var isInside = false;
    function nodeList(nodes) {
        //TODO:Check
        return nodes.map(function (node) {
            if (node.getNode) {
                return node.localName();
            }
            return node.localName;
        });
    }

    function copyAttributes(xPathEngine, srcNode, destNode) {
        var attributes = srcNode.selectNodes('@*');
        for (var i = 1; i <= attributes.length; i += 1) {
            // NOTE: We cannot handle the case where an optional attribute is missing from the source.
            //		 This will cause the code to fail with an exception.

            // Skip xsi:nil on destination.
            var srcAttr = srcNode.selectSingle('@*[position() = ' + i + ']');
            //TODO:Check NamespaceUri
            if (srcNode.namespaceURI() === nsXsi) {
                continue;
            }

            var destAttr = selectNodeFromXPathEngine(
                xPathEngine,
                '@*[position() = ' + i + ']',
                destNode,
            );

            setNodeValue(destAttr, srcAttr.value());
        }
    }

    function copyElements(srcNode, destNode) {
        var srcChildren = srcNode.selectNodes('*');

        var destChildren = getChildElements(destNode);

        srcChildren.forEach(function (srcChild, i) {
            copyNodeContent(srcChild.getNode(), destChildren[i]);
        });
        //TODO: Else condition not implemented in C#.
    }

    function testNodeStructure(xPathEngine, srcNode, destNode, isInsideRow) {
        var srcNodes = srcNode.selectNodes(
            `*/*[namespace-uri() != '${XHTML_NAMESPACE}']`,
        );
        if (srcNodes.length) {
            throw createError(
                'InvalidOperationException',
                copyTableErrorDeepTables,
            );
        }

        var srcAttributes = srcNode.selectNodes(
                String.format("@*[namespace-uri() != '{0}']", nsXsi),
            ),
            destAttributes = selectNodeListFromXPathEngine(
                xPathEngine,
                destNode,
                String.format("@*[namespace-uri() != '{0}']", nsXsi),
            );

        if (srcAttributes.length !== destAttributes.length) {
            const srcList = nodeList(srcAttributes),
                destList = nodeList(destAttributes);

            throw createError(
                'InvalidOperationException',
                copyTableErrorStructureMismatch,
                String.format(
                    'Source attribute count: {0}. Destination attribute count: {1}. Source attribute name list: {2}. Destination attribute name list: {3}.',
                    srcAttributes.length,
                    destAttributes.length,
                    srcList.join(),
                    destList.join(),
                ),
            );
        }

        if (!isInsideRow) {
            var srcElements = srcNode.selectNodes('*'),
                destElements = selectNodeListFromXPathEngine(
                    xPathEngine,
                    destNode,
                    '*',
                );

            if (srcElements.length !== destElements.length) {
                const srcList = nodeList(srcElements),
                    destList = nodeList(destElements);

                throw createError(
                    'InvalidOperationException',
                    copyTableErrorStructureMismatch,
                    String.format(
                        'Source element count: {0}. Destination element count: {1}. Source element name list: {2}. Destination element name list: {3}.',
                        srcElements.length,
                        destElements.length,
                        srcList.join(),
                        destList.join(),
                    ),
                );
            }
        }
    }

    function copyRow(xPathEngine, srcNode, destNode) {
        testNodeStructure(xPathEngine, srcNode, destNode, isInside);
        copyElements(srcNode, destNode, isInside);
        copyAttributes(xPathEngine, srcNode, destNode);

        if (isInside) {
            return;
        }

        isInside = true;

        var elements = srcNode.selectNodes('*');
        for (var i = 1; i <= elements.length; i += 1) {
            // Skip xsi:nil on destination.
            var srcElement = srcNode.selectSingle('*[position() = ' + i + ']'),
                destElement = selectNodeFromXPathEngine(
                    xPathEngine,
                    '*[position() = ' + i + ']',
                    destNode,
                );

            if (destElement) {
                copyRow(xPathEngine, srcElement, destElement);
            }
        }
        isInside = false;
    }

    function copyRowsAsync(srcNodes, destRow, xPathEngine, isMoving) {
        var count = 0;
        var addList = []; // List of rows to add to dest DOM. Add at end in case of error along the way.
        var deleteList = []; // List of rows to delete from source DOM. See below for details.

        try {
            for (const srcNode of srcNodes) {
                var destNode = destRow.cloneNode(true);
                isInside = false;

                copyRow(xPathEngine, srcNode, destNode);
                addList.push(destNode.cloneNode(true));

                if (isMoving) {
                    deleteList.push(srcNode.cloneNode(true));
                }

                count += 1;
            }

            return {
                count: count,
                addList: addList,
                deleteList: deleteList,
            };
        } catch (e) {
            throw new Error(
                'An error occurred while copying rows: ' + e.message,
            );
        }
    }

    function getDestRowAsync(dsnamedest, xpathdest) {
        return params.sampleDataSource.initAsync().then(function (sampleData) {
            var destRow = sampleData.selectSampleNode(dsnamedest, xpathdest);

            if (!destRow) {
                throw new Error(
                    String.format(
                        copyTableErrorFailedToSelectDestinationRow,
                        cmd,
                    ),
                );
            }

            return destRow;
        });
    }

    async function addRowsToDestAsync(destTables, addList, rowdest, empty) {
        try {
            for (const destTable of destTables) {
                if (empty) {
                    deleteNodes(destTable, rowdest);
                }

                if (isEmpty(addList)) {
                    continue;
                }

                const childrenToAdd = addList.map((n) => n.cloneNode(true));

                //AddNils(dsnamedest, node);//TODO:AddNils Functionality
                await destTable.appendChildrenAsync(childrenToAdd);
            }
        } catch (e) {
            throw new Error(copyTableErrorFailedToAdd);
        }
    }

    function deleteSourceNodes(deleteList) {
        try {
            // Delete queued up nodes.
            if (deleteList.length) {
                deleteList.forEach(function (node) {
                    node.deleteSelf();
                });
            }
        } catch (e) {
            throw new Error(copyTableErrorFailedToDelete);
        }
    }

    async function executeAsync() {
        const xPathEngine = params.xpathEngine;
        const dsnamesrc = cf.getParamValue(paramDsNameSrc);
        const tablesrc = cf.getParamValue(paramTableSrc);
        const rowsrc = cf.getParamValue(paramRowSrc);
        const dsnamedest = cf.getParamValue(paramDsNameDest);
        const tabledest = cf.getParamValue(paramTableDest);
        const rowdest = cf.getParamValue(paramRowDest);
        const full_rowdest =
            tabledest +
            (tabledest[tabledest.length - 1] === '/' ? '' : '/') +
            rowdest;
        const empty = cf.getBoolParamValue(paramEmpty);
        //allow use of xpathdest to get the destination row
        const xpathdest = cf.getParamValue(paramXpathDest) || full_rowdest;
        const domSource = cf.getDataSource(dsnamesrc);
        const domDest = cf.getDataSource(dsnamedest);

        var sourceTable = getValidNode(
            domSource,
            tablesrc,
            String.format(copyTableErrorFailedToSelectSourceTable, cmd),
        );
        var destTables = getValidNodeSet(
            domDest,
            tabledest,
            String.format(copyTableErrorFailedToSelectDestinationTable, cmd),
        );
        var sourceRows = getValidNodeSet(
            sourceTable,
            rowsrc,
            String.format(copyTableErrorFailedToSelectSourceRow, cmd),
        );

        const destRow = await getDestRowAsync(dsnamedest, xpathdest);

        const copyResult = copyRowsAsync(
            sourceRows,
            destRow,
            xPathEngine,
            isMove,
        );

        await addRowsToDestAsync(
            destTables,
            copyResult.addList,
            rowdest,
            empty,
        );

        deleteSourceNodes(copyResult.deleteList);

        return success(copyResult.count);
    }

    return {
        description,
        optionalParameters,
        requiredParameters,
        executeAsync,
    };
}

export default CopyTable;
